import { useEffect, useState } from 'react';
/**
 * Keeps a component mounted long enough to animate itself out.
 *
 * Conditional rendering removes the element the instant the flag flips, so an
 * exit animation never gets a chance to run. This holds it in the tree, flips
 * a `data-state` the CSS can key off, and unmounts once the animation is done.
 *
 * Honours `prefers-reduced-motion` by unmounting immediately.
 */
export function usePresence(isOpen, durationMs = 220) {
    const [mounted, setMounted] = useState(isOpen);
    const [state, setState] = useState(isOpen ? 'open' : 'closed');
    useEffect(() => {
        if (isOpen) {
            setMounted(true);
            // A frame's delay so the browser paints the closed state first —
            // otherwise the enter animation has nothing to animate from.
            const frame = requestAnimationFrame(() => setState('open'));
            return () => cancelAnimationFrame(frame);
        }
        setState('closed');
        const timer = setTimeout(() => setMounted(false), prefersReducedMotion() ? 0 : durationMs);
        return () => clearTimeout(timer);
    }, [isOpen, durationMs]);
    return { mounted, state };
}
function prefersReducedMotion() {
    if (typeof window === 'undefined' || !window.matchMedia)
        return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
